import {Inject, Injectable, PLATFORM_ID} from "@angular/core";
import {ActivatedRouteSnapshot, NavigationEnd, Router} from "@angular/router";
import {isPlatformBrowser} from "@angular/common";
import {filter} from "rxjs/operators";
import * as Sentry from "@sentry/angular-ivy";

/**
 * Route params that are safe to attach to an event. Deliberately an allowlist rather than "every
 * param": consumer routes are id-based today, but a future `:email` style param would otherwise
 * start shipping seller PII to Sentry silently. A missing tag is a harmless failure; a leaked
 * address is not.
 */
const TAGGED_ROUTE_PARAMS = ["propertyId", "contactId", "journeyId", "purlId", "postcardId", "widgetId", "landingPageConfigId", "jobId"];

@Injectable({
  providedIn: "root"
})
export class SentryContextService {
  constructor(
    private router: Router,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => this.applyContext());
  }

  private applyContext(): void {
    const chain = this.buildRouteChain();

    const params: Record<string, string> = {};
    chain.forEach(snapshot =>
      TAGGED_ROUTE_PARAMS.forEach(param => {
        const value = snapshot.paramMap.get(param);
        if (value) {
          params[param] = value;
        }
      })
    );

    Sentry.setTags({
      ...params,
      // The agency this page belongs to. Nothing else on the settings payloads identifies a tenant,
      // and an untagged error on a multi-tenant app is close to unactionable.
      subdomain: this.findSubdomain(chain) ?? "unknown"
    });

    // The only identity a consumer visitor has. No name, email or address is ever attached.
    Sentry.setUser(params["contactId"] ? {id: params["contactId"]} : null);
  }

  private buildRouteChain(): ActivatedRouteSnapshot[] {
    const chain: ActivatedRouteSnapshot[] = [];
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (snapshot) {
      chain.push(snapshot);
      snapshot = snapshot.firstChild;
    }
    return chain;
  }

  private findSubdomain(chain: ActivatedRouteSnapshot[]): string | undefined {
    // Every resolver on this app returns a settings payload, and `subdomain` is the one field they
    // all carry. It is optional on the landing page response, so treat a miss as expected.
    for (const snapshot of chain) {
      for (const resolved of Object.values(snapshot.data)) {
        const subdomain = (resolved as {subdomain?: unknown} | null)?.subdomain;
        if (typeof subdomain === "string" && subdomain) {
          return subdomain;
        }
      }
    }
    return undefined;
  }
}
